/**
 * Gestionnaire d'activité de session
 * Ce module surveille l'activité de l'utilisateur et met à jour l'horodatage
 * de la dernière activité pour gérer l'expiration de session par inactivité.
 */

import { getTokenManager } from '../../hooks/getTokenManager';

// Liste des événements à surveiller pour détecter l'activité de l'utilisateur
const ACTIVITY_EVENTS = [
  'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart',
  'click', 'dblclick', 'keypress', 'touchmove', 'wheel'
];

// Délai minimal entre les mises à jour de l'activité (en ms)
// Pour éviter de mettre à jour trop fréquemment
const UPDATE_THROTTLE_MS = 60000; // 1 minute
let lastUpdateTime = 0;

/**
 * Initialise le suivi d'activité de l'utilisateur
 * Cette fonction doit être appelée au chargement de l'application
 */
export function initSessionActivityTracking(): void {
  if (typeof window === 'undefined') return; // S'assurer qu'on est côté client
  
  console.log('🔐 Initialisation du suivi d\'activité de session');
  
  // Fonction de gestion des événements d'activité
  const handleUserActivity = async () => {
    const now = Date.now();
    
    // Ne mettre à jour que si suffisamment de temps s'est écoulé depuis la dernière mise à jour
    if (now - lastUpdateTime > UPDATE_THROTTLE_MS) {
      lastUpdateTime = now;
      try {
        const TokenManager = await getTokenManager();
        TokenManager.updateLastActivity();
      } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'activité:', error);
      }
    }
  };
  
  // Ajouter des écouteurs pour tous les événements d'activité
  ACTIVITY_EVENTS.forEach(eventType => {
    window.addEventListener(eventType, handleUserActivity, { passive: true });
  });
  
  // Vérifier périodiquement si la session a expiré
  const checkSessionInterval = setInterval(async () => {
    try {
      const TokenManager = await getTokenManager();
      if (TokenManager.isAuthenticated()) {
        console.log('🔐 Vérification périodique de l\'expiration de session');
      }
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'authentification:', error);
    }
  }, UPDATE_THROTTLE_MS);
  
  // Nettoyer les écouteurs d'événements lorsque la page est déchargée
  window.addEventListener('beforeunload', () => {
    ACTIVITY_EVENTS.forEach(eventType => {
      window.removeEventListener(eventType, handleUserActivity);
    });
    clearInterval(checkSessionInterval);
  });
  
  // Initialiser l'horodatage de la dernière activité
  getTokenManager().then((TokenManager: any) => {
    TokenManager.updateLastActivity();
  }).catch((error: any) => {
    console.error('Erreur lors de l\'initialisation de l\'activité:', error);
  });
}

/**
 * Vérifie si la session a expiré et gère la déconnexion si nécessaire
 * Cette fonction peut être appelée avant les opérations critiques
 * @returns {boolean} true si la session est valide, false si elle a expiré
 */
export async function checkSessionValidity(): Promise<boolean> {
  try {
    const TokenManager: any = await getTokenManager();
    
    if (TokenManager.hasSessionExpired()) {
      console.log('🔐 Session expirée, redirection vers la page de connexion');
      
      // Déconnecter l'utilisateur
      TokenManager.clearTokens();
      
      // Rediriger vers la page de connexion si nous sommes côté client
      if (typeof window !== 'undefined') {
        window.location.href = `/login?session_expired=${Date.now()}`;
      }
      
      return false;
    }
    
    return true;
  } catch (error: any) {
    console.error('Erreur lors de la vérification de la validité de session:', error);
    return true; // Par défaut, considérer la session comme valide en cas d'erreur
  }
}
